export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

export interface ThemePalette {
  name: string;
  primary: ColorScale;
  secondary: ColorScale;
}

/** As duas escalas que a comunidade personaliza. */
export type ScaleName = 'primary' | 'secondary';

export type ShadeKey = keyof ColorScale;

export const SCALE_NAMES: ScaleName[] = ['primary', 'secondary'];

export const SHADE_KEYS: ShadeKey[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

/**
 * Paleta que a comunidade guarda na API (`Community.themePalette`).
 * Nula = usar o padrão do produto, que vive no `index.css`.
 */
export type CommunityPalette = Record<ScaleName, ColorScale>;

/**
 * Espelho exato dos valores chumbados em `src/index.css`.
 * É o ponto de partida do editor de cores e o alvo do "restaurar padrão".
 * Se mudar lá, mude aqui.
 */
export const DEFAULT_COMMUNITY_PALETTE: CommunityPalette = {
  primary: {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#be185d',
    800: '#9d174d',
    900: '#831843',
    950: '#500724',
  },
  secondary: {
    50: '#fff1f2',
    100: '#ffe4e6',
    200: '#fecdd3',
    300: '#fda4af',
    400: '#fb7185',
    500: '#f43f5e',
    600: '#e11d48',
    700: '#be123c',
    800: '#9f1239',
    900: '#881337',
    950: '#4c0519',
  },
};

/**
 * Shades the interface actually paints today, found by sweeping the
 * `*-primary-*` / `*-secondary-*` classes in `src/`. They are highlighted in
 * the editor — the rest exist to complete the scale.
 */
export const SHADES_IN_USE: Record<ScaleName, ShadeKey[]> = {
  primary: [50, 100, 200, 500, 600, 700, 800, 900],
  secondary: [600, 700],
};

/**
 * Shades used as a solid background with white text on top. These are the ones
 * that have to pass the contrast check — if the community picks a light shade
 * here, the text disappears.
 */
export const SHADES_WITH_WHITE_TEXT: Record<ScaleName, ShadeKey[]> = {
  primary: [500, 600, 700],
  secondary: [600, 700],
};

const HEX_PATTERN = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

/** Normaliza um hexadecimal para `#rrggbb` minúsculo. Null se for inválido. */
export function normalizeHex(value: string): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!HEX_PATTERN.test(trimmed)) return null;
  let hex = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (hex.length === 3) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  return `#${hex.toLowerCase()}`;
}

export function isHexColor(value: string): boolean {
  return normalizeHex(value) !== null;
}

function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const normalized = normalizeHex(hex);
  if (!normalized) {
    throw new Error('Invalid hex color');
  }

  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToHex(h: number, s: number, l: number): string {
  const sat = s / 100;
  const lig = l / 100;

  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lig - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Derives the 11 shades from a base color (which becomes shade 500).
 * It has to keep mirroring `memberfy-api/src/utils/colorPalette.js` — the same
 * curve on both sides, so that the "generate shades" shortcut on screen and
 * the server's filling in reach the same result.
 *
 * A light shade never ends up darker than the base, and a dark one never ends
 * up lighter: the lightness of the dark shades is a fraction of the base's, not
 * an absolute value. With an absolute floor, an already dark base produced a
 * 700 lighter than the 600.
 */
export function generateColorScale(baseColor: string): ColorScale {
  const { h, s, l } = hexToHSL(baseColor);
  const base = normalizeHex(baseColor) as string;

  const lighter = (saturation: number, target: number) => hslToHex(h, saturation, Math.max(target, l));
  const darker = (saturation: number, factor: number) => hslToHex(h, saturation, Math.round(l * factor));

  return {
    50: lighter(Math.min(s + 10, 100), 97),
    100: lighter(Math.min(s + 10, 100), 94),
    200: lighter(Math.min(s + 5, 100), 87),
    300: lighter(s, 77),
    400: lighter(s, 65),
    500: base,
    600: darker(s, 0.85),
    700: darker(Math.max(s - 5, 0), 0.68),
    800: darker(Math.max(s - 10, 0), 0.52),
    900: darker(Math.max(s - 15, 0), 0.38),
    950: darker(Math.max(s - 20, 0), 0.28),
  };
}

/* ------------------------------------------------------------------ */
/* Contraste                                                           */
/* ------------------------------------------------------------------ */

function relativeLuminance(hex: string): number {
  const normalized = normalizeHex(hex);
  if (!normalized) return 0;
  const channels = [1, 3, 5].map((i) => {
    const v = parseInt(normalized.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** Razão de contraste WCAG entre duas cores (1 a 21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export const WCAG_AA_NORMAL = 4.5;

/** Cor de texto legível sobre o fundo dado: branco ou o cinza-900 da casa. */
export function readableForeground(background: string): string {
  return contrastRatio(background, '#ffffff') >= contrastRatio(background, '#111827')
    ? '#ffffff'
    : '#111827';
}

/**
 * Darkens the color until it reaches the requested contrast ratio against
 * white. Returns the color itself when it already passes, or the darkest shade
 * possible when even pure black would not (does not happen at 4.5, but it is
 * cheap to have).
 */
export function darkenUntilContrast(hex: string, minRatio = WCAG_AA_NORMAL, against = '#ffffff'): string {
  const normalized = normalizeHex(hex);
  if (!normalized) return hex;
  if (contrastRatio(normalized, against) >= minRatio) return normalized;

  const { h, s, l } = hexToHSL(normalized);
  for (let lightness = l - 1; lightness >= 0; lightness -= 1) {
    const candidate = hslToHex(h, s, lightness);
    if (contrastRatio(candidate, against) >= minRatio) return candidate;
  }
  return '#000000';
}

/* ------------------------------------------------------------------ */
/* Aplicação em runtime                                                */
/* ------------------------------------------------------------------ */

/** Nome da CSS custom property de um tom. */
export function shadeVarName(scale: ScaleName, shade: ShadeKey): string {
  return `--color-${scale}-${shade}`;
}

/** Nome da CSS custom property da cor de texto sobre a escala. */
export function foregroundVarName(scale: ScaleName): string {
  return `--color-${scale}-foreground`;
}

/**
 * Name of the RGB-channel property of a shade ("r g b"), the format the F-191
 * tokens use so Tailwind opacity modifiers work. Written beside the hex one.
 */
export function shadeChannelVarName(scale: ScaleName, shade: ShadeKey): string {
  return `--rgb-${scale}-${shade}`;
}

/** Property of the community color used as text/link on the surface. */
export const ACCENT_TEXT_VAR = '--rgb-accent-text';

/** "#db2777" -> "219 39 119". */
export function hexToChannels(hex: string): string | null {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  return [1, 3, 5].map((i) => parseInt(normalized.slice(i, i + 2), 16)).join(' ');
}

/** First primary shade from 600 that reads (4.5:1) on white; 900 when none does. */
export function accentTextShade(scale: ColorScale): string {
  const shades: ShadeKey[] = [600, 700, 800, 900];
  const passing = shades.find((shade) => contrastRatio(scale[shade], '#ffffff') >= WCAG_AA_NORMAL);
  return scale[passing ?? 900];
}

/* ------------------------------------------------------------------ */
/* Dark theme (F-191 phase 1)                                          */
/* ------------------------------------------------------------------ */

/** The dark theme's card surface, the `--rgb-white` of `:root.dark` in index.css. */
export const DARK_SURFACE = '#17171c';

export type ThemeMode = 'light' | 'dark';

/** `a` over `b` at `weight` (0 to 1), in sRGB. */
function mixHex(a: string, b: string, weight: number): string {
  const ca = hexToChannels(a)?.split(' ').map(Number) ?? [0, 0, 0];
  const cb = hexToChannels(b)?.split(' ').map(Number) ?? [0, 0, 0];
  return `#${ca.map((value, i) => Math.round(value * weight + cb[i] * (1 - weight)).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * A scale as the dark theme paints it, the same rule index.css applies to the
 * status hues: the tints (50-200) become dark tints of the deep shades, the
 * text shades (700-950) take the light ones, and 400-600 stay, so a solid
 * button keeps its color and its `--color-*-foreground`.
 */
export function darkScaleOf(scale: ColorScale): ColorScale {
  return {
    50: mixHex(scale[950], DARK_SURFACE, 0.5),
    100: mixHex(scale[900], DARK_SURFACE, 0.6),
    200: mixHex(scale[800], DARK_SURFACE, 0.7),
    300: scale[700],
    400: scale[400],
    500: scale[500],
    600: scale[600],
    700: scale[300],
    800: scale[200],
    900: scale[100],
    950: scale[50],
  };
}

/** The community color as text on the dark surface: the first shade from 600 up to 100 that reads (4.5:1). */
export function darkAccentTextShade(scale: ColorScale): string {
  const shades: ShadeKey[] = [600, 500, 400, 300, 200];
  const passing = shades.find((shade) => contrastRatio(scale[shade], DARK_SURFACE) >= WCAG_AA_NORMAL);
  return scale[passing ?? 100];
}

/**
 * Turns the palette into the map of CSS custom properties.
 * Good both for applying on `:root` and for previewing inside an isolated
 * block — a custom property inherits, so an inline `style` on a `<div>` swaps
 * the colors only in there.
 */
export function paletteToCssVars(palette: CommunityPalette, mode: ThemeMode = 'light'): Record<string, string> {
  const vars: Record<string, string> = {};

  SCALE_NAMES.forEach((scale) => {
    const shades = palette[scale] && mode === 'dark' ? darkScaleOf(palette[scale]) : palette[scale];
    if (!shades) return;
    SHADE_KEYS.forEach((shade) => {
      const value = normalizeHex(String(shades[shade] ?? ''));
      if (value) {
        vars[shadeVarName(scale, shade)] = value;
        vars[shadeChannelVarName(scale, shade)] = hexToChannels(value) as string;
      }
    });
    // Text on a solid button follows shade 600, which is the background of
    // those buttons. Without this, a light community color erases the text.
    const solid = normalizeHex(String(shades[600] ?? ''));
    if (solid) vars[foregroundVarName(scale)] = readableForeground(solid);
  });

  if (palette.primary) {
    const accent = hexToChannels(mode === 'dark' ? darkAccentTextShade(palette.primary) : accentTextShade(palette.primary));
    if (accent) vars[ACCENT_TEXT_VAR] = accent;
  }

  return vars;
}

/** The id of the `<style>` that carries the community palette (light and dark). */
const PALETTE_STYLE_ID = 'community-palette';

const cssBlock = (selector: string, vars: Record<string, string>) =>
  `${selector} {\n${Object.entries(vars).map(([name, value]) => `  ${name}: ${value};`).join('\n')}\n}`;

/** The palette arrives from the API as loose JSON. Only complete ones pass. */
export function parseCommunityPalette(value: unknown): CommunityPalette | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Record<string, unknown>;
  const result: Partial<CommunityPalette> = {};

  SCALE_NAMES.forEach((scale) => {
    const shades = input[scale];
    if (!shades || typeof shades !== 'object') return;
    const source = shades as Record<string, unknown>;
    const parsed: Partial<ColorScale> = {};
    let complete = true;
    SHADE_KEYS.forEach((shade) => {
      const hex = normalizeHex(String(source[shade] ?? ''));
      if (!hex) complete = false;
      else parsed[shade] = hex;
    });
    if (complete) result[scale] = parsed as ColorScale;
  });

  return Object.keys(result).length > 0 ? (result as CommunityPalette) : null;
}

/**
 * Applies (or removes) the community palette on `:root`.
 *
 * Passing `null` clears the inline properties and hands control back to
 * `index.css` — which is what keeps a community with no configuration looking
 * exactly as it does today.
 */
/**
 * The address bar color on mobile — the `<meta name="theme-color">`.
 *
 * It comes from the palette, not from a field of its own. Settings used to have
 * a "Theme Color" promising exactly this and **nothing read it**: the meta tag
 * was never written, and the community picked a color that went nowhere. Two
 * sources for one decision, one of them decorative.
 *
 * Shade 600 is the same one the management bar and the primary buttons use: the
 * browser chrome meets the top of the page instead of fighting it.
 */
function applyBrowserThemeColor(palette: CommunityPalette | null): void {
  /* With no palette the community is not left colorless: it uses the product's,
     which is what `index.css` paints. Removing the meta would leave a white bar
     above a screen that is not white. */
  const cor = palette?.primary?.[600] ?? DEFAULT_COMMUNITY_PALETTE.primary[600];
  let meta = document.querySelector('meta[name="theme-color"]');

  if (!cor) {
    meta?.remove();
    return;
  }
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', cor);
}

export function applyCommunityPalette(palette: CommunityPalette | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  /* Inline properties (the legacy theme picker, older builds) would beat the
     dark block below, so they go first. */
  SCALE_NAMES.forEach((scale) => {
    SHADE_KEYS.forEach((shade) => {
      root.style.removeProperty(shadeVarName(scale, shade));
      root.style.removeProperty(shadeChannelVarName(scale, shade));
    });
    root.style.removeProperty(foregroundVarName(scale));
  });
  root.style.removeProperty(ACCENT_TEXT_VAR);

  /* F-191 phase 1: a stylesheet instead of inline properties, so the palette
     has a light block (also for `theme-light` islands) and a dark one that
     `:root.dark` picks. Without a palette it goes away and index.css answers. */
  let style = document.getElementById(PALETTE_STYLE_ID);
  if (!palette) {
    style?.remove();
    applyBrowserThemeColor(null);
    return;
  }
  if (!style) {
    style = document.createElement('style');
    style.id = PALETTE_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = `${cssBlock(':root,\n.theme-light', paletteToCssVars(palette))}\n${cssBlock(':root.dark', paletteToCssVars(palette, 'dark'))}`;
  applyBrowserThemeColor(palette);
}

/**
 * Reads the palette currently in force from `:root`. It is the editor's
 * starting point: a community with no configuration opens the screen on today's
 * colors, not on an empty white.
 */
export function readAppliedPalette(): CommunityPalette {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return DEFAULT_COMMUNITY_PALETTE;
  }

  /* Read inside a `theme-light` probe: in the dark theme `:root` holds the dark scale, not the one the community saved. */
  const probe = document.createElement('div');
  probe.className = 'theme-light';
  probe.hidden = true;
  document.body.appendChild(probe);
  const computed = window.getComputedStyle(probe);
  const result: Partial<CommunityPalette> = {};

  SCALE_NAMES.forEach((scale) => {
    const shades: Partial<ColorScale> = {};
    SHADE_KEYS.forEach((shade) => {
      const raw = computed.getPropertyValue(shadeVarName(scale, shade));
      shades[shade] = normalizeHex(raw) ?? DEFAULT_COMMUNITY_PALETTE[scale][shade];
    });
    result[scale] = shades as ColorScale;
  });
  probe.remove();

  return result as CommunityPalette;
}

/* ------------------------------------------------------------------ */
/* Compatibilidade com o seletor de temas prontos                      */
/* ------------------------------------------------------------------ */

export function applyTheme(palette: ThemePalette): void {
  const root = document.documentElement;

  Object.entries(palette.primary).forEach(([key, value]) => {
    root.style.setProperty(`--color-primary-${key}`, value);
    const channels = hexToChannels(value);
    if (channels) root.style.setProperty(`--rgb-primary-${key}`, channels);
  });

  Object.entries(palette.secondary).forEach(([key, value]) => {
    root.style.setProperty(`--color-secondary-${key}`, value);
    const channels = hexToChannels(value);
    if (channels) root.style.setProperty(`--rgb-secondary-${key}`, channels);
  });

  const accent = hexToChannels(accentTextShade(palette.primary));
  if (accent) root.style.setProperty(ACCENT_TEXT_VAR, accent);

  localStorage.setItem('memberfy-theme', palette.name);
}

export function getContrastColor(hexColor: string): 'light' | 'dark' {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
  if (!result) {
    return 'dark';
  }

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? 'dark' : 'light';
}
